import { enforceGridPrecisionInDeg, enforceGridPrecisionInMm, enforceGridPrecisionInRad } from "../pcb";
import { normalizeDegreeOrientation } from "./photonEngineHelpers";
import { convertAngle, metersToCm, metersToInch, metersToMm, metersToUm } from "./unitConversion";
export function humanizeLengthValue(value, unitValue) {
    switch (unitValue) {
        case "mm": {
            return `${enforceGridPrecisionInMm(metersToMm(value))}mm`;
        }
        case "cm": {
            return `${enforceGridPrecisionInMm(metersToCm(value))}cm`;
        }
        case "inch": {
            return `${enforceGridPrecisionInMm(metersToInch(value))}inch`;
        }
        case "um": {
            return `${enforceGridPrecisionInMm(metersToUm(value))}um`;
        }
        default: {
            return `${enforceGridPrecisionInMm(metersToMm(value))}mm`;
        }
    }
}
export function humanizeRotationValue(valueInRad, unitValue) {
    if (!unitValue || unitValue === "deg") {
        const degreeValue = convertAngle(valueInRad, "rad", "deg");
        const normalizedDegreeValue = normalizeDegreeOrientation(degreeValue);
        return `${enforceGridPrecisionInDeg(normalizedDegreeValue)}deg`;
    }
    else if (unitValue === "rad") {
        const normalizedRadValue = enforceGridPrecisionInRad(valueInRad);
        return `${normalizedRadValue}rad`;
    }
    return undefined;
}
export function humanizeScalarValue(value) {
    return `${value ?? 0}`;
}
/**
 * Humanize a vector object delimited by spaces
 * @param humanizer The function to humanize a single value
 * @param value The Vector object to be humanized
 * @param unitValue Dimension unit (mm, cm, deg, red, etc.)
 * @param includeZ (Optional) If true, include the Z value in the humanized string, even if it's 0. If false, exclude the Z value even if it's not 0. If undefined, include the Z value only if it's not 0.
 * @returns A space-delimited string with humanized values
 * @see humanizeLengthValue (for humanizer)
 * @see humanizeRotationValue (for humanizer)
 */
export function humanizeVectorValue(humanizer, value, unitValue, includeZ) {
    // Prepare a two-dimensional vector array
    const vector = [value.x ?? 0, value.y ?? 0];
    // If includeZ is true, include the Z value in the humanized string, even if it's 0.
    // If includeZ is false, exclude the Z value even if it's not 0.
    // If includeZ is undefined, include the Z value only if it's not 0.
    if (includeZ || (includeZ === undefined && !!value.z))
        vector.push(value.z ?? 0);
    return vector
        .map((val) => Number(val) || 0) // map to 0 if NaN
        .map((val) => humanizer(val, unitValue))
        .join(" ");
}
/**
 * Dedupe array of humanized elements
 * @param vector Vector (array of 1--3 elements) which needs to be deduped
 * @returns Deduped array
 */
export const dedupeHumanizedVector = (vector) => {
    const dedupedHumanized = [...new Set(vector?.split(" "))];
    if (dedupedHumanized.length === 1) {
        return dedupedHumanized[0];
    }
    return vector;
};
//# sourceMappingURL=humanizeValues.js.map